import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import './ProfilePage.scss';

interface Room {
  reservationId: string;
  guestName: string;
  roomNumber?: string | null;
  checkIn: string;
  checkOut: string;
  status: string;
  /** A room of the booker's own booking (they look after it). */
  managed: boolean;
  /** pre_arrival | in_house | post_stay */
  stage: string;
  /** Its stay is open to guests now (before arrival, in house, or within the post-stay window). */
  canShare: boolean;
  folio?: { balance: number; totalAmount: number; currencySymbol: string } | null;
}

interface RoomsResponse {
  canRegister: boolean;
  rooms: Room[];
}

interface Form {
  name: string;
  phone: string;
  email: string;
  noContact: boolean;
}

/**
 * "My rooms": the main booker's view of the other rooms of their booking that share their phone or email. For each:
 * its bill, a link/QR so the guests in it can use their own phones, or "give this room its own contact" — then it
 * stands on its own (own access, own WhatsApp, own profile and points).
 */
export default function MyRoomsPage() {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const { hotelSlug } = useParams<{ hotelSlug: string }>();
  const [data, setData] = useState<RoomsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, Form>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const tr = (key: string, fallback: string, params?: Record<string, string | number>) => {
    const v = t(key, params);
    if (v && v !== key) return v;
    return params ? Object.entries(params).reduce((s, [k, val]) => s.split(`{{${k}}}`).join(String(val)), fallback) : fallback;
  };
  const fmt = (d: string) => new Date(d).toLocaleDateString(language, { day: 'numeric', month: 'short' });
  const goWelcome = () => navigate(hotelSlug ? `/${hotelSlug}/welcome` : '/welcome');

  const load = (signal?: AbortSignal) =>
    apiClient.get<RoomsResponse>('/api/v1/guest/rooms', { signal })
      .then(({ data: res }) => setData(res))
      .catch(e => { if (e?.name !== 'AbortError') setError(tr('rooms.loadError', 'Could not load your rooms.')); })
      .finally(() => setLoading(false));

  useEffect(() => {
    const controller = new AbortController();
    window.scrollTo(0, 0);
    load(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareAccess = async (room: Room) => {
    setError(null);
    try {
      const { data: res } = await apiClient.post<{ url: string }>(`/api/v1/guest/rooms/${room.reservationId}/share-link`, {});
      setShare(s => ({ ...s, [room.reservationId]: res.url }));
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || tr('rooms.saveError', 'Could not save. Please check the details.'));
    }
  };

  const sendLink = async (room: Room, url: string) => {
    const text = tr('rooms.shareText', 'Your access to room {{room}}', { room: room.roomNumber ?? '' });
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); return; } catch { /* fall back to copy */ }
    }
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    setCopied(room.reservationId);
    setTimeout(() => setCopied(null), 2000);
  };

  const openForm = (room: Room) =>
    setForm(f => ({ ...f, [room.reservationId]: f[room.reservationId] ?? { name: '', phone: '', email: '', noContact: false } }));
  const updateForm = (id: string, patch: Partial<Form>) => setForm(f => ({ ...f, [id]: { ...f[id], ...patch } }));

  const saveOwnContact = async (room: Room) => {
    const f = form[room.reservationId];
    setSaving(room.reservationId);
    setError(null);
    try {
      await apiClient.post('/api/v1/guest/rooms', {
        rooms: [{
          reservationId: room.reservationId,
          guestName: f.name.trim(),
          phone: f.noContact ? null : f.phone.trim() || null,
          email: f.noContact ? null : f.email.trim() || null,
          noContact: f.noContact,
        }],
      });
      setForm(fs => { const n = { ...fs }; delete n[room.reservationId]; return n; });
      setLoading(true);
      await load();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message || tr('rooms.saveError', 'Could not save. Please check the details.'));
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <PageSpinner />;

  return (
    <div className="page-content profile-page">
      <PageHeader title={tr('rooms.myTitle', 'My rooms')} />
      {error && <div className="profile-error">{error}</div>}

      {!data || data.rooms.length === 0 ? (
        <div className="profile-join">
          <p>{tr('rooms.noneLeft', 'Every room on your booking has its own guest now.')}</p>
          <button className="profile-btn profile-btn--ghost" onClick={goWelcome}>{tr('common.back', 'Back')}</button>
        </div>
      ) : !data.canRegister ? (
        <div className="profile-join">
          <p>{tr('rooms.verifyFirst', 'Verify your phone number on this device to register your rooms.')}</p>
          <button className="profile-btn profile-btn--ghost" onClick={goWelcome}>{tr('common.back', 'Back')}</button>
        </div>
      ) : (
        <>
          <p className="profile-intro">
            {tr('rooms.myIntro', 'These rooms are on your booking and use your phone or email. Share each room\'s access with the guests staying there, or give a room its own contact so its guest gets their own access and earns their own points.')}
          </p>
          {data.rooms.map(room => {
            const f = form[room.reservationId];
            const url = share[room.reservationId];
            return (
              <div key={room.reservationId} className="profile-card profile-card--form">
                <div className="profile-row">
                  <div>
                    <div className="profile-row__title">
                      {room.roomNumber ? `${tr('rooms.room', 'Room')} ${room.roomNumber}` : room.guestName}
                    </div>
                    <div className="profile-row__sub">{room.guestName} · {fmt(room.checkIn)} – {fmt(room.checkOut)}</div>
                  </div>
                  {room.folio && (
                    <span className="profile-row__meta" title={tr('rooms.bill', 'Bill')}>
                      {room.folio.currencySymbol}{room.folio.balance.toLocaleString(language)}
                    </span>
                  )}
                </div>

                {!room.managed && (
                  <p className="profile-row__sub">{tr('rooms.otherBooking', 'This room is on another booking with your contact — give it its own contact.')}</p>
                )}

                {room.managed && (url ? (
                  <div className="profile-share">
                    <QRCodeSVG value={url} size={168} />
                    <p className="profile-row__sub">{tr('rooms.shareHint', 'Let the guests in this room scan this, or send them the link to open on their own phone.')}</p>
                    <button className="profile-btn profile-btn--primary" onClick={() => sendLink(room, url)}>
                      {copied === room.reservationId ? tr('rooms.copied', 'Link copied') : tr('rooms.sendLink', 'Send the link')}
                    </button>
                  </div>
                ) : room.canShare ? (
                  <button className="profile-btn profile-btn--primary" onClick={() => shareAccess(room)}>{tr('rooms.share', 'Share access')}</button>
                ) : (
                  <p className="profile-row__sub">{tr('rooms.shareUnavailable', 'Access to this room isn\'t available right now.')}</p>
                ))}

                {f ? (
                  <>
                    <label className="profile-field">
                      <span className="profile-field__label">{tr('rooms.guestName', "Guest's name")}</span>
                      <input value={f.name} onChange={e => updateForm(room.reservationId, { name: e.target.value })} />
                    </label>
                    {!f.noContact && (
                      <>
                        <label className="profile-field">
                          <span className="profile-field__label">{tr('profile.phone', 'Phone')}</span>
                          <input type="tel" dir="ltr" value={f.phone} onChange={e => updateForm(room.reservationId, { phone: e.target.value })} />
                        </label>
                        <label className="profile-field">
                          <span className="profile-field__label">{tr('profile.email', 'Email')}</span>
                          <input type="email" dir="ltr" value={f.email} onChange={e => updateForm(room.reservationId, { email: e.target.value })} />
                        </label>
                      </>
                    )}
                    <label className="profile-check">
                      <input type="checkbox" checked={f.noContact} onChange={e => updateForm(room.reservationId, { noContact: e.target.checked })} />
                      <span>{tr('rooms.noContact', 'No phone or email (for example, a child) — I will look after this room')}</span>
                    </label>
                    <button className="profile-btn profile-btn--primary" onClick={() => saveOwnContact(room)}
                      disabled={saving === room.reservationId || !f.name.trim() || (!f.noContact && !f.phone.trim() && !f.email.trim())}>
                      {saving === room.reservationId ? tr('common.loading', 'Loading…') : tr('rooms.save', 'Save')}
                    </button>
                  </>
                ) : (
                  <button className="profile-btn profile-btn--ghost" onClick={() => openForm(room)}>
                    {tr('rooms.ownContact', 'Give this room its own contact')}
                  </button>
                )}
              </div>
            );
          })}
          <div className="profile-actions">
            <button className="profile-btn profile-btn--ghost" onClick={goWelcome}>{tr('common.back', 'Back')}</button>
          </div>
        </>
      )}
    </div>
  );
}
